import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
  Sparkles, Plane, TrainFront, Milestone, GraduationCap, School,
  HeartPulse, ShoppingBag, Landmark, Factory, Trees, TrendingUp,
  AlertTriangle, Loader2, RefreshCw, TrainTrack, Construction,
  IndianRupee, Lightbulb, Share2, Copy, Check, Building2,
  Layers, Zap, BarChart3, MapPin, X, Maximize2, CheckCircle2,
  FileText, Download,
} from 'lucide-react';
import HighwayRadialMap from './HighwayRadialMap';
import HTSatelliteMap from './HTSatelliteMap';
import InvestmentIntelligenceDashboard from './InvestmentIntelligenceDashboard';
import WaterBodyBlock from './WaterBodyBlock';
import generateSiteClearancePdf from '../../lib/siteClearancePdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Small POI category → lucide icon map so the card feels visual.
const POI_ICON = {
  airport: Plane,
  railway: TrainFront,
  highway: Milestone,
  school: School,
  college: GraduationCap,
  hospital: HeartPulse,
  shopping: ShoppingBag,
  government: Landmark,
  industrial: Factory,
  tourism: Trees,
  metro: TrainTrack,
  future: Construction,
};

const ROAD_TONE = {
  Undeveloped: 'text-red-700 bg-red-50 border-red-200',
  Developing: 'text-amber-700 bg-amber-50 border-amber-200',
  'Well-connected': 'text-emerald-700 bg-emerald-50 border-emerald-200',
  'Highway-grade': 'text-emerald-800 bg-emerald-100 border-emerald-300',
};

// ------------------------------------------------------------------------
// Site Clearance CTA — one-tap branded PDF that bundles HT + water verdicts
// on Acre Mitra letterhead, ready to attach to a buyer's deal packet.
// ------------------------------------------------------------------------
const SiteClearanceCta = ({ property, ht, waterbody, htLoading, waterbodyLoading }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const disabled = busy || (htLoading && waterbodyLoading);
  const htV = ht?.verdict || 'unknown';
  const wbV = waterbody?.verdict || 'unknown';
  const TONE = {
    critical: 'bg-rose-100 text-rose-800 border-rose-300',
    warning:  'bg-amber-100 text-amber-800 border-amber-300',
    info:     'bg-sky-100 text-sky-800 border-sky-300',
    clear:    'bg-emerald-100 text-emerald-800 border-emerald-300',
    unknown:  'bg-slate-100 text-slate-700 border-slate-300',
  };
  const download = async () => {
    setBusy(true); setError(null);
    try {
      await generateSiteClearancePdf({ property, ht, waterbody, output: 'save' });
    } catch (e) {
      setError('Could not generate the certificate. Refresh and try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-amber-50 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-start gap-3 shadow-sm" data-testid="site-clearance-cta">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white grid place-items-center shadow shrink-0">
          <FileText size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-800">Site clearance certificate</div>
            <span className={`text-[9px] font-black uppercase tracking-widest border rounded-full px-2 py-0.5 ${TONE[htV] || TONE.unknown}`}>HT · {htV}</span>
            <span className={`text-[9px] font-black uppercase tracking-widest border rounded-full px-2 py-0.5 ${TONE[wbV] || TONE.unknown}`}>Water · {wbV}</span>
          </div>
          <div className="text-sm font-black text-slate-900 mt-1 leading-tight">
            Printable due-diligence summary — HT lines + water bodies clearance
          </div>
          <div className="text-[11px] text-slate-600 mt-1 leading-relaxed">
            Attach this to any invoice, reservation letter or bank loan file. Includes the verdict, the nearest features, distances and a signed data-source disclaimer.
          </div>
          {error && <div className="text-[10px] text-rose-700 mt-1 font-bold">{error}</div>}
        </div>
      </div>
      <button
        type="button"
        onClick={download}
        disabled={disabled}
        className="w-full sm:w-auto shrink-0 inline-flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black text-xs px-4 py-2.5 rounded-full shadow"
        data-testid="site-clearance-download"
      >
        {busy ? <><Loader2 size={14} className="animate-spin" /> Building…</> : <><Download size={14} /> Download PDF</>}
      </button>
    </div>
  );
};

// Customer-facing card: 5-year ROI graph, road development score, nearby
// POIs (airport / schools / hospitals) and suggested industrial uses.
export const LocalityReportCard = ({ property }) => {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [ht, setHt] = useState({ loading: true, error: null, data: null });
  const [waterbody, setWaterbody] = useState({ loading: false, error: null, data: null });
  const [similar, setSimilar] = useState({ loading: true, error: null, data: null });
  const [similarRadius, setSimilarRadius] = useState(120);

  const load = async () => {
    setState({ loading: true, error: null, data: null });
    try {
      const res = await axios.get(`${API}/properties/${property.id}/locality-report`);
      setState({ loading: false, error: null, data: res.data });
    } catch (err) {
      setState({
        loading: false,
        error: err.response?.data?.detail || 'Could not load AI report right now.',
        data: null,
      });
    }
  };

  const loadHt = async ({ refresh = false } = {}) => {
    setHt({ loading: true, error: null, data: null });
    try {
      const res = await axios.get(`${API}/properties/${property.id}/ht-wire-report`, {
        params: refresh ? { refresh: true } : {},
      });
      setHt({ loading: false, error: null, data: res.data });
    } catch (err) {
      setHt({
        loading: false,
        error: err.response?.data?.detail || 'Could not scan HT-wire corridor right now.',
        data: null,
      });
    }
  };

  const loadWaterbody = async ({ refresh = false } = {}) => {
    setWaterbody({ loading: true, error: null, data: null });
    try {
      const res = await axios.get(`${API}/properties/${property.id}/waterbody-report`, {
        params: refresh ? { refresh: true } : {},
        timeout: 45000,
      });
      setWaterbody({ loading: false, error: null, data: res.data });
    } catch (err) {
      setWaterbody({
        loading: false,
        error: err.response?.data?.detail || 'Could not scan water bodies right now.',
        data: null,
      });
    }
  };

  const loadSimilar = async (radiusKm = similarRadius) => {
    setSimilar({ loading: true, error: null, data: null });
    try {
      const res = await axios.get(`${API}/properties/${property.id}/similar-comparables`, {
        params: { radius: radiusKm },
      });
      setSimilar({ loading: false, error: null, data: res.data });
    } catch (err) {
      setSimilar({
        loading: false,
        error: err.response?.data?.detail || 'Could not compute local comparables.',
        data: null,
      });
    }
  };

  const changeSimilarRadius = (r) => {
    setSimilarRadius(r);
    loadSimilar(r);
  };

  useEffect(() => {
    load();
    loadHt();
    loadWaterbody();
    loadSimilar(120);
  }, [property.id]);

  return (
    <div
      className="rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 via-white to-emerald-50 p-5"
      data-testid="locality-report-card"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center shrink-0">
          </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
            Investment Intelligence
            <span className="text-[9px] font-bold uppercase tracking-widest bg-emerald-700 text-white px-1.5 py-0.5 rounded-full">
              Free
            </span>
          </h3>
          <p className="text-xs text-slate-600 mt-0.5">
            10-yr ROI projection, why-invest thesis and nearby infrastructure — computed live
            from this plot&rsquo;s pinned coordinates.
          </p>
        </div>
      </div>

      {state.loading && (
        <div className="flex flex-col items-center justify-center py-10 text-slate-500" data-testid="locality-report-loading">
          <Loader2 size={22} className="animate-spin mb-2" />
          <div className="text-xs">Computing locality intelligence…</div>
        </div>
      )}

      {state.error && !state.loading && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700 flex items-start gap-2" data-testid="locality-report-error">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold">Report unavailable</div>
            <div className="text-xs mt-0.5">{state.error}</div>
          </div>
          <button
            onClick={load}
            className="text-xs font-bold text-emerald-700 hover:text-emerald-900 underline flex items-center gap-1"
            data-testid="locality-report-retry"
          >
            <RefreshCw size={11} /> Retry
          </button>
        </div>
      )}

      {state.data && !state.loading && !state.error && (
        <div data-testid="locality-report-body">
          {/* Thesis */}
          {state.data.summary && (
            <p className="text-sm text-slate-700 leading-relaxed border-l-4 border-amber-400 pl-3 bg-white/60 py-2 rounded-r-lg">
              {state.data.summary}
            </p>
          )}

          {/* Current → 10-year projected price hero */}
          {state.data.projected_price && (
            <ProjectedPriceHero pp={state.data.projected_price} property={property} />
          )}

          {/* Fresh dashboard visualisation: KPI + donut + bar race */}
          {state.data.projected_price && (
            <InvestmentIntelligenceDashboard
              projected={state.data.projected_price}
              roi={state.data.roi_projection}
              rental={state.data.rental_estimate}
            />
          )}

          {/* Rental / lease income estimate */}
          {state.data.rental_estimate && (
            <RentalEstimateBlock rental={state.data.rental_estimate} kind={state.data.property_kind} />
          )}

          {/* Airport Authority (AAI) vs Municipal Corporation floor permissions */}
          {(state.data.airport_authority_rule || state.data.airport_height_limit || state.data.fsi_estimate) && (
            <ConstructionLimitsBlock
              airport={state.data.airport_authority_rule || state.data.airport_height_limit}
              municipal={state.data.municipal_permission}
              fsi={state.data.fsi_estimate}
              kind={state.data.property_kind}
            />
          )}

          {/* HIGH TENSION power-line scan (OSM Overpass, text-only) */}
          <HighTensionBlock
            state={ht}
            onRetry={loadHt}
            centerLat={parseFloat(property.latitude)}
            centerLng={parseFloat(property.longitude)}
            waterbodies={waterbody?.data?.features || []}
          />

          {/* WATER BODIES proximity scan (plot / land listings only) */}
          {(() => {
            const t = String(property.type || property.property_type || '').toLowerCase();
            const isPlot = t.includes('plot') || t.includes('land') || t.includes('agri') || t === '';
            if (!isPlot) return null;
            return <WaterBodyBlock propertyId={property.id} preloaded={waterbody} onRetry={loadWaterbody} />;
          })()}

          {/* Site Clearance Certificate — one-tap branded PDF summarising the
              HT + water body verdicts, ready to slot into the buyer's deal packet. */}
          <SiteClearanceCta
            property={property}
            ht={ht?.data}
            waterbody={waterbody?.data}
            htLoading={ht?.loading}
            waterbodyLoading={waterbody?.loading}
          />

          {/* SIMILAR PLOTS ₹/sqft bar chart */}
          <SimilarPlotsBlock
            state={similar}
            onRetry={() => loadSimilar(similarRadius)}
            radius={similarRadius}
            onChangeRadius={changeSimilarRadius}
          />

          {/* Commercial project ideas + state benchmark (land only) */}
          {state.data.commercial_ideas && (
            <CommercialIdeasBlock
              ideas={state.data.commercial_ideas}
              benchmark={state.data.state_commercial_benchmark}
            />
          )}

          {/* Flat / house — resale + rent focus (built residential only) */}
          {state.data.flat_house_investment && (
            <FlatHouseInvestmentBlock investment={state.data.flat_house_investment} />
          )}

          {/* ROI graph */}
          <ROIGraph roi={state.data.roi_projection} basePrice={state.data.projected_price?.current_price} />

          {/* Why invest here */}
          {Array.isArray(state.data.why_invest) && state.data.why_invest.length > 0 && (
            <WhyInvestBlock reasons={state.data.why_invest} />
          )}

          {/* Road development score */}
          {state.data.road_development && (
            <RoadDevelopmentBlock
              road={state.data.road_development}
              propertyLabel={property?.title || 'This plot'}
            />
          )}

          {/* Future infrastructure — growth catalysts */}
          {Array.isArray(state.data.future_infra) && state.data.future_infra.length > 0 && (
            <FutureInfraBlock items={state.data.future_infra} />
          )}

          {/* Nearby POIs */}
          {Array.isArray(state.data.poi) && state.data.poi.length > 0 && (
            <div className="mt-5">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
                Nearby infrastructure
              </div>
              <ul className="grid sm:grid-cols-2 gap-2" data-testid="locality-report-poi-list">
                {state.data.poi.map((p, i) => {
                  const Icon = POI_ICON[p.category] || Milestone;
                  const isFuture = p.category === 'future';
                  return (
                    <li
                      key={`poi-${i}`}
                      className={`rounded-xl border px-3 py-2 flex items-center gap-3 min-w-0 ${
                        isFuture
                          ? 'bg-amber-50 border-amber-300'
                          : 'bg-white border-slate-200'
                      }`}
                    >
                      <span
                        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border ${
                          isFuture
                            ? 'bg-amber-100 text-amber-800 border-amber-300'
                            : 'bg-emerald-50 text-emerald-700 border-emerald-100'
                        }`}
                      >
                        <Icon size={14} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-semibold truncate ${isFuture ? 'text-amber-900' : 'text-slate-900'}`}>
                          {p.name}
                        </div>
                        <div className={`text-[10px] uppercase tracking-widest ${isFuture ? 'text-amber-700 font-bold' : 'text-slate-500'}`}>
                          {isFuture ? 'Upcoming · Gov. announced' : p.category}
                        </div>
                      </div>
                      <span className={`text-xs font-bold whitespace-nowrap shrink-0 ${isFuture ? 'text-amber-800' : 'text-emerald-700'}`}>
                        {typeof p.distance_km === 'number' ? `${p.distance_km.toFixed(1)} km` : '—'}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Suggested industrial / commercial uses */}
          {Array.isArray(state.data.suggested_use_cases) && state.data.suggested_use_cases.length > 0 && (
            <div className="mt-5">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">
                Best-fit industrial projects
              </div>
              <div className="flex flex-wrap gap-2" data-testid="locality-report-usecases">
                {state.data.suggested_use_cases.map((u, i) => (
                  <span
                    key={`use-${i}`}
                    className="text-xs bg-emerald-100 text-emerald-800 border border-emerald-200 px-2.5 py-1 rounded-full font-semibold"
                  >
                    {u}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Risks */}
          {Array.isArray(state.data.risks) && state.data.risks.length > 0 && (
            <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800 mb-1 flex items-center gap-1">
                <AlertTriangle size={11} /> Risks to double-check on-ground
              </div>
              <ul className="text-xs text-amber-900 list-disc pl-5 space-y-0.5" data-testid="locality-report-risks">
                {state.data.risks.map((r, i) => <li key={`risk-${i}`}>{r}</li>)}
              </ul>
            </div>
          )}

          {state.data.disclaimer && (
            <p className="text-[10px] text-slate-400 mt-4 italic">
              {state.data.disclaimer}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ---- ROI 10-yr projection chart ----
const formatINR = (n) => {
  if (n == null || Number.isNaN(n)) return '';
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(1)}k`;
  return `₹${Math.round(n)}`;
};

const ROIGraph = ({ roi, basePrice }) => {
  if (!roi || !Array.isArray(roi.yearly) || roi.yearly.length === 0) return null;
  const cagr = typeof roi.cagr_percent === 'number' ? roi.cagr_percent : null;
  const years = roi.years_covered || roi.yearly.length;
  // If basePrice is available, enrich each datapoint with ₹ value so the
  // tooltip can show real rupees, not just an index.
  const data = roi.yearly.map((d) => ({
    ...d,
    priceValue: basePrice ? Math.round((basePrice * d.value) / 100) : null,
  }));
  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-xl p-4" data-testid="locality-report-roi">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <div className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
          <TrendingUp size={15} className="text-emerald-700" /> {years}-year appreciation
        </div>
        {cagr !== null && (
          <div className="text-xs text-slate-600">
            Est. CAGR
            <span className="ml-1 text-emerald-700 font-bold" data-testid="locality-report-cagr">
              {cagr.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
      <div style={{ width: '100%', height: 176 }}>
        <ResponsiveContainer width="99%" height={176}>
          <LineChart data={data} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="year" tick={{ fontSize: 11, fill: '#64748b' }} />
            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} domain={['auto', 'auto']} />
            <Tooltip
              formatter={(v, _n, entry) => {
                if (entry?.payload?.priceValue) {
                  return [`${formatINR(entry.payload.priceValue)} (index ${v})`, 'Value'];
                }
                return [`Index ${v}`, 'Value'];
              }}
              labelStyle={{ color: '#0f172a', fontWeight: 700 }}
              contentStyle={{ borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12 }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#047857"
              strokeWidth={2.5}
              dot={{ r: 3, fill: '#047857' }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[10px] text-slate-500 mt-1">
        Base = 100 at {roi.current_year || '2026'}. Confidence: <strong className="capitalize">{roi.confidence || 'medium'}</strong>.
        {basePrice ? ' Hover a year to see the projected ₹ value.' : ''}
      </div>
    </div>
  );
};

// ---- Current → 10-year projected price hero ----
const buildShareMessage = (pp, property) => {
  const title = property?.title || 'this plot';
  const location = property?.location ? ` in ${property.location}` : '';
  // Prefer the human-readable /property/<code> deep-link (matches the route
  // in App.js). Fall back to a UUID query param if the property has no code.
  const propertyUrl = property?.property_code
    ? `${window.location.origin}/property/${property.property_code}`
    : `${window.location.origin}/?property=${property?.id || ''}`;
  return (
    `🏡 *Investment Opportunity — ${title}${location}*\n\n` +
    `💰 *Today:* ${formatINR(pp.current_price)}\n` +
    `📈 *In ${pp.years} years:* ${formatINR(pp.projected_price)}\n` +
    `✨ *Est. profit:* +${formatINR(pp.profit)} (${pp.multiple.toFixed(2)}× money)\n\n` +
    `📊 Projected CAGR: ${pp.cagr_percent.toFixed(1)}% p.a.\n` +
    `🔎 Calibrated from real geospatial data — no guesswork.\n\n` +
    `View full report on AcreMitra:\n${propertyUrl}\n\n` +
    `— Shared via AcreMitra 🌱`
  );
};

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback for older browsers / non-secure contexts.
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

const isMobileUA = () =>
  typeof navigator !== 'undefined' && /iphone|ipad|ipod|android/i.test(navigator.userAgent || '');

const ProjectedPriceHero = ({ pp, property }) => {
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);

  // Primary handler: attempts native share on mobile, otherwise copies to
  // clipboard and opens WhatsApp so the user pastes into any chat. WhatsApp
  // dropped support for the anonymous `wa.me/?text=...` contact-picker flow,
  // so this two-step approach is now the only reliable cross-platform path.
  const handleShare = async () => {
    const msg = buildShareMessage(pp, property);

    // 1. Native share sheet (best UX on mobile + some desktop browsers).
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${property?.title || 'Property'} — 10-yr projection`,
          text: msg,
        });
        setShared(true);
        setTimeout(() => setShared(false), 1800);
        return;
      } catch (err) {
        // User cancelled the share sheet — respect their choice, do nothing.
        if (err && err.name === 'AbortError') return;
        // Any other error: fall through to the copy + WhatsApp fallback.
      }
    }

    // 2. Fallback — copy to clipboard, then open WhatsApp so user pastes.
    const ok = await copyToClipboard(msg);
    setShared(ok);
    setTimeout(() => setShared(false), 2400);

    // Open WhatsApp: mobile app deep-link on phones, WhatsApp Web on desktop.
    // Both open with an empty chat window; the message is already on the
    // clipboard so user just long-press/paste in whichever chat they want.
    const url = isMobileUA()
      ? 'whatsapp://send'
      : 'https://web.whatsapp.com/';
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCopy = async () => {
    const msg = buildShareMessage(pp, property);
    const ok = await copyToClipboard(msg);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  };

  return (
    <div className="mt-4 rounded-xl bg-gradient-to-br from-emerald-700 to-emerald-800 text-white p-4 shadow-lg" data-testid="locality-report-price-hero">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-100 mb-2">
        <IndianRupee size={11} /> 10-year projected value
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-emerald-200">Today</div>
          <div className="text-lg sm:text-xl font-black text-white" data-testid="pp-current">{formatINR(pp.current_price)}</div>
        </div>
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-wider text-emerald-200">In {pp.years} yrs</div>
          <div className="text-lg sm:text-xl font-black text-amber-300" data-testid="pp-projected">{formatINR(pp.projected_price)}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-emerald-200">Est. profit</div>
          <div className="text-lg sm:text-xl font-black text-amber-300" data-testid="pp-profit">+{formatINR(pp.profit)}</div>
        </div>
      </div>
      <div className="mt-2 text-[11px] text-emerald-100 leading-relaxed">
        At an estimated {pp.cagr_percent.toFixed(1)}% CAGR your money compounds{' '}
        <strong className="text-white">{pp.multiple.toFixed(2)}×</strong> in {pp.years} years — assuming India&rsquo;s
        tier-2/3 land historical trend continues.
      </div>

      {/* Share row — proven referral driver in real-estate flows */}
      <div className="mt-3 pt-3 border-t border-emerald-600/40 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <div className="text-[11px] text-emerald-100 flex-1 leading-snug">
          {shared
            ? '✅ Message copied — paste it into any WhatsApp chat.'
            : 'Know a friend hunting land? Send them this projection.'}
        </div>
        <div className="flex gap-1.5 shrink-0">
          <button
            type="button"
            onClick={handleShare}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-emerald-800 text-xs font-bold shadow hover:shadow-lg hover:bg-amber-50 transition-all active:scale-95"
            data-testid="share-projection-btn"
            aria-label="Share this projection via WhatsApp"
          >
            {shared ? <Check size={13} /> : <Share2 size={13} />}
            <span>{shared ? 'Copied!' : 'Share via WhatsApp'}</span>
          </button>
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-emerald-600/40 hover:bg-emerald-500/60 text-white transition-all active:scale-95"
            data-testid="copy-projection-btn"
            aria-label={copied ? 'Copied!' : 'Copy projection to clipboard'}
            title={copied ? 'Copied!' : 'Copy to clipboard'}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- Rental / lease income estimate ----
const KIND_LABEL = {
  land: 'Land',
  flat_house: 'Residential unit',
  commercial_building: 'Commercial building',
};

const RentalEstimateBlock = ({ rental, kind }) => (
  <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="locality-report-rental">
    <div className="flex items-center gap-2 mb-2">
      <IndianRupee size={16} className="text-amber-700" />
      <h4 className="text-sm font-black text-amber-900">Monthly rent / lease potential</h4>
    </div>
    <div className="text-[11px] uppercase tracking-widest text-amber-700 font-bold mb-1">
      Best-fit for {KIND_LABEL[kind] || 'this property'}
    </div>
    <div className="text-sm font-semibold text-amber-900 mb-2">{rental.best_fit}</div>
    <div className="grid grid-cols-2 gap-3">
      <div className="rounded-lg bg-white border border-amber-200 p-2">
        <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Conservative</div>
        <div className="text-lg font-black text-amber-900">{formatINR(rental.monthly_low)}<span className="text-xs font-normal">/mo</span></div>
        <div className="text-[10px] text-amber-700">₹{rental.per_sqft_low}/sqft/month</div>
      </div>
      <div className="rounded-lg bg-white border border-amber-200 p-2">
        <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Upside</div>
        <div className="text-lg font-black text-amber-900">{formatINR(rental.monthly_high)}<span className="text-xs font-normal">/mo</span></div>
        <div className="text-[10px] text-amber-700">₹{rental.per_sqft_high}/sqft/month</div>
      </div>
    </div>
    <div className="text-[10px] text-amber-800 mt-2 leading-relaxed">
      Based on {rental.area_sqft.toLocaleString()} sqft usable area. {rental.notes}
    </div>
  </div>
);

// ---- Airport Authority vs Municipal Corporation floor permissions ----
const ZONE_STYLE = {
  strict:       { bg: 'bg-rose-50',    br: 'border-rose-200',    tag: 'bg-rose-600',    text: 'text-rose-700',    label: 'Strict cap' },
  moderate:     { bg: 'bg-amber-50',   br: 'border-amber-200',   tag: 'bg-amber-600',   text: 'text-amber-800',   label: 'Gradual cap' },
  flexible:     { bg: 'bg-sky-50',     br: 'border-sky-200',     tag: 'bg-sky-600',     text: 'text-sky-800',     label: 'NOC > G+10' },
  unrestricted: { bg: 'bg-emerald-50', br: 'border-emerald-200', tag: 'bg-emerald-600', text: 'text-emerald-800', label: 'No AAI cap' },
};

const ConstructionLimitsBlock = ({ airport, municipal, fsi, kind }) => {
  if (!airport && !municipal && !fsi) return null;

  const relevance = kind === 'commercial_building' || kind === 'land'
    ? 'crucial-for-builder'
    : 'informational';

  const zoneKey = (airport?.restriction_level) || (airport?.applies === false ? 'unrestricted' : 'flexible');
  const zStyle = ZONE_STYLE[zoneKey] || ZONE_STYLE.flexible;

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="locality-report-limits">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Building2 size={16} className="text-slate-700" />
        <h4 className="text-sm font-black text-slate-900">Floor permission — Airport vs Municipal</h4>
        {relevance === 'crucial-for-builder' && (
          <span className="text-[9px] uppercase tracking-widest bg-emerald-700 text-white px-1.5 py-0.5 rounded font-bold">
            key for builders
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* --- AIRPORT AUTHORITY (AAI) --- */}
        {airport && (
          <div
            className={`rounded-lg bg-white border ${zStyle.br} p-3 flex flex-col gap-2`}
            data-testid="airport-authority-card"
          >
            <div className="flex items-center gap-2">
              <span className={`w-9 h-9 rounded-lg ${zStyle.bg} ${zStyle.text} border ${zStyle.br} flex items-center justify-center shrink-0`}>
                <Plane size={16} />
              </span>
              <div className="min-w-0">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                  Airport Authority of India (AAI)
                </div>
                <div className="text-sm font-black text-slate-900">
                  {airport.zone}
                </div>
              </div>
              <span className={`ml-auto text-[9px] uppercase tracking-widest ${zStyle.tag} text-white px-1.5 py-0.5 rounded font-bold shrink-0`}>
                {zStyle.label}
              </span>
            </div>

            <div className="text-xs text-slate-700" data-testid="airport-height-zone">
              <span className="font-semibold">{airport.distance_km} km</span> from{' '}
              <span className="font-semibold">
                {airport.airport_name || 'nearest airport'}
              </span>{' '}
              (Aerodrome Reference Point).
            </div>

            {airport.max_floors ? (
              <div className="text-sm text-slate-800">
                Height cap:{' '}
                <strong className={zStyle.text}>
                  ~{airport.max_meters} m (G+{airport.max_floors})
                </strong>
              </div>
            ) : airport.applies ? (
              <div className="text-sm text-slate-800">
                No fixed AAI ceiling —{' '}
                <strong className={zStyle.text}>NOC required only for G+10 &amp; above</strong>.
              </div>
            ) : (
              <div className="text-sm text-emerald-700 font-semibold">
                Outside 20 km AAI zone — no airport height restriction.
              </div>
            )}

            <div className="flex flex-wrap gap-1.5">
              {airport.noc_required ? (
                <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded font-bold">
                  AAI NOC required{airport.noc_threshold_m ? ` > ${airport.noc_threshold_m} m` : ''}
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-300 px-1.5 py-0.5 rounded font-bold">
                  AAI NOC not required
                </span>
              )}
            </div>

            <div className="text-[11px] text-slate-600 leading-relaxed">
              {airport.notes}
            </div>
          </div>
        )}

        {/* --- MUNICIPAL CORPORATION --- */}
        {(municipal || fsi) && (
          <div
            className="rounded-lg bg-white border border-emerald-200 p-3 flex flex-col gap-2"
            data-testid="municipal-permission-card"
          >
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center shrink-0">
                <Layers size={16} />
              </span>
              <div className="min-w-0">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                  Municipal Corporation / Dev. Authority
                </div>
                <div className="text-sm font-black text-slate-900">
                  {municipal?.authority || fsi?.band || 'Municipal FSI rule'}
                </div>
              </div>
              <span className="ml-auto text-[9px] uppercase tracking-widest bg-emerald-700 text-white px-1.5 py-0.5 rounded font-bold shrink-0">
                Local rule
              </span>
            </div>

            <div className="text-xs text-slate-700">
              FSI band:{' '}
              <span className="font-semibold text-emerald-700">
                {(municipal?.fsi_low ?? fsi?.fsi_low)} – {(municipal?.fsi_high ?? fsi?.fsi_high)}
              </span>
              {municipal?.band ? ` · ${municipal.band}` : ''}
            </div>

            {municipal ? (
              <div className="text-sm text-slate-800">
                Permissible envelope:{' '}
                <strong className="text-emerald-700">
                  G+{municipal.typical_floors_low} – G+{municipal.typical_floors_high}
                </strong>{' '}
                (~{municipal.typical_height_low_m}–{municipal.typical_height_high_m} m)
              </div>
            ) : null}

            {municipal?.overridden_by_airport && (
              <div className="text-[11px] font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                ⚠ Airport rule overrides — actual buildable height is capped by AAI.
              </div>
            )}

            <div className="text-[11px] text-slate-600 leading-relaxed">
              {municipal?.notes || fsi?.notes}
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 text-[10px] text-slate-500 leading-relaxed">
        Distances computed by Haversine formula from the property pin to the nearest airport&apos;s ARP.
        Municipal envelope assumes ~40% ground coverage and 3.2 m floor-to-floor — always verify
        with the local Development Authority DCR before finalising a project.
      </div>
    </div>
  );
};

// ---- High-Tension power-line scan (text-only info block) ----
const HT_VERDICT_STYLE = {
  critical: { bg: 'bg-rose-50',    br: 'border-rose-300',    tag: 'bg-rose-600',    text: 'text-rose-800',    dot: 'bg-rose-500' },
  warning:  { bg: 'bg-amber-50',   br: 'border-amber-300',   tag: 'bg-amber-600',   text: 'text-amber-800',   dot: 'bg-amber-500' },
  info:     { bg: 'bg-sky-50',     br: 'border-sky-200',     tag: 'bg-sky-600',     text: 'text-sky-800',     dot: 'bg-sky-500' },
  clear:    { bg: 'bg-emerald-50', br: 'border-emerald-200', tag: 'bg-emerald-600', text: 'text-emerald-800', dot: 'bg-emerald-500' },
};

const HT_VOLTAGE_FILTERS = [
  { key: 'all',   label: 'All',      minKv: 0 },
  { key: '33',    label: '33 kV+',   minKv: 33 },
  { key: '66',    label: '66 kV+',   minKv: 66 },
  { key: '132',   label: '132 kV+',  minKv: 132 },
  { key: '220',   label: '220 kV+',  minKv: 220 },
];

/**
 * Imperatively draws the HT corridor map into an <svg> DOM node. Shared by
 * the compact mini-map on the property card and the full-screen zoom modal
 * so both stay visually identical (rings, compass, colour-coded polylines,
 * plot pin) while allowing different sizes + optional interactive lines.
 */
function drawHTMap(svg, opts) {
  const {
    centerLat, centerLng, lines, radiusM = 2000, size = 260,
    onLineClick, highlightedId,
  } = opts;
  if (!svg) return;
  const NS = 'http://www.w3.org/2000/svg';
  while (svg.firstChild) svg.removeChild(svg.firstChild);

  const PAD = Math.max(16, size * 0.06);
  const CX = size / 2;
  const CY = size / 2;
  const R = (size / 2) - PAD;
  const scale = R / radiusM;

  const M_PER_DEG_LAT = 111_320.0;
  const M_PER_DEG_LNG = 111_320.0 * Math.cos((centerLat * Math.PI) / 180);
  const project = (lat, lng) => ({
    x: CX + ((lng - centerLng) * M_PER_DEG_LNG) * scale,
    y: CY - ((lat - centerLat) * M_PER_DEG_LAT) * scale,
  });
  const lineColor = (l) => {
    if (l.distance_m <= 100) return '#e11d48';
    if (l.distance_m <= 500) return '#d97706';
    return '#0284c7';
  };

  // --- defs (gradient + grid pattern) ---
  const defs = document.createElementNS(NS, 'defs');
  const gridSize = Math.max(12, Math.round(size / 13));
  const pat = document.createElementNS(NS, 'pattern');
  pat.setAttribute('id', `ht-grid-${size}`);
  pat.setAttribute('width', gridSize);
  pat.setAttribute('height', gridSize);
  pat.setAttribute('patternUnits', 'userSpaceOnUse');
  const patPath = document.createElementNS(NS, 'path');
  patPath.setAttribute('d', `M${gridSize} 0H0V${gridSize}`);
  patPath.setAttribute('fill', 'none');
  patPath.setAttribute('stroke', '#e2e8f0');
  patPath.setAttribute('stroke-width', '1');
  pat.appendChild(patPath);
  defs.appendChild(pat);
  const rg = document.createElementNS(NS, 'radialGradient');
  rg.setAttribute('id', `ht-bg-${size}`);
  rg.setAttribute('cx', '50%');
  rg.setAttribute('cy', '50%');
  rg.setAttribute('r', '50%');
  const stop1 = document.createElementNS(NS, 'stop');
  stop1.setAttribute('offset', '0%');
  stop1.setAttribute('stop-color', '#f8fafc');
  const stop2 = document.createElementNS(NS, 'stop');
  stop2.setAttribute('offset', '100%');
  stop2.setAttribute('stop-color', '#e2e8f0');
  rg.appendChild(stop1);
  rg.appendChild(stop2);
  defs.appendChild(rg);
  svg.appendChild(defs);

  // Background circles
  const bg = document.createElementNS(NS, 'circle');
  bg.setAttribute('cx', CX); bg.setAttribute('cy', CY); bg.setAttribute('r', R);
  bg.setAttribute('fill', `url(#ht-bg-${size})`);
  svg.appendChild(bg);
  const gridBg = document.createElementNS(NS, 'circle');
  gridBg.setAttribute('cx', CX); gridBg.setAttribute('cy', CY); gridBg.setAttribute('r', R);
  gridBg.setAttribute('fill', `url(#ht-grid-${size})`);
  svg.appendChild(gridBg);

  // Distance rings + labels
  [500, 1000, 2000].forEach((m) => {
    const ring = document.createElementNS(NS, 'circle');
    ring.setAttribute('cx', CX); ring.setAttribute('cy', CY);
    ring.setAttribute('r', m * scale);
    ring.setAttribute('fill', 'none');
    ring.setAttribute('stroke', '#64748b');
    ring.setAttribute('stroke-width', size >= 400 ? '1.25' : '1');
    ring.setAttribute('stroke-dasharray', '4 3');
    ring.setAttribute('opacity', '0.85');
    svg.appendChild(ring);

    const lbl = document.createElementNS(NS, 'text');
    lbl.setAttribute('x', CX + m * scale - 4);
    lbl.setAttribute('y', CY - 4);
    lbl.setAttribute('font-size', size >= 400 ? '13' : '9');
    lbl.setAttribute('fill', '#334155');
    lbl.setAttribute('text-anchor', 'end');
    lbl.setAttribute('font-weight', '700');
    lbl.textContent = m >= 1000 ? `${m / 1000} km` : `${m} m`;
    svg.appendChild(lbl);
  });

  // Compass N/E/S/W
  const cFont = size >= 400 ? '14' : '10';
  [
    { t: 'N', x: CX, y: PAD - 2, anchor: 'middle' },
    { t: 'S', x: CX, y: size - 2, anchor: 'middle' },
    { t: 'E', x: size - 2, y: CY + 4, anchor: 'end' },
    { t: 'W', x: 2, y: CY + 4, anchor: 'start' },
  ].forEach(({ t, x, y, anchor }) => {
    const c = document.createElementNS(NS, 'text');
    c.setAttribute('x', x); c.setAttribute('y', y);
    c.setAttribute('font-size', cFont);
    c.setAttribute('font-weight', '900');
    c.setAttribute('fill', '#475569');
    c.setAttribute('text-anchor', anchor);
    c.textContent = t;
    svg.appendChild(c);
  });

  // HT polylines — sorted farthest→closest so closest paints on top
  const shown = (lines || []).slice(0, 8).slice().sort((a, b) => b.distance_m - a.distance_m);
  shown.forEach((ln) => {
    const geom = ln.geometry || [];
    if (geom.length < 2) return;
    const pts = geom
      .map(([la, lo]) => {
        const { x, y } = project(la, lo);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
    const color = lineColor(ln);
    const isHi = highlightedId != null && ln.id === highlightedId;

    // halo
    const halo = document.createElementNS(NS, 'polyline');
    halo.setAttribute('points', pts);
    halo.setAttribute('fill', 'none');
    halo.setAttribute('stroke', color);
    halo.setAttribute('stroke-opacity', isHi ? '0.55' : '0.28');
    halo.setAttribute('stroke-width', size >= 400 ? (isHi ? '14' : '10') : '6');
    halo.setAttribute('stroke-linecap', 'round');
    if (onLineClick) halo.style.pointerEvents = 'none';
    svg.appendChild(halo);

    // core (this is the clickable target in interactive mode)
    const core = document.createElementNS(NS, 'polyline');
    core.setAttribute('points', pts);
    core.setAttribute('fill', 'none');
    core.setAttribute('stroke', color);
    core.setAttribute('stroke-width', size >= 400 ? (isHi ? '5' : '3.4') : '2.4');
    core.setAttribute('stroke-linecap', 'round');
    if (onLineClick) {
      // Extra fat invisible hit area for touch UX
      const hit = document.createElementNS(NS, 'polyline');
      hit.setAttribute('points', pts);
      hit.setAttribute('fill', 'none');
      hit.setAttribute('stroke', 'transparent');
      hit.setAttribute('stroke-width', size >= 400 ? '22' : '14');
      hit.setAttribute('stroke-linecap', 'round');
      hit.style.cursor = 'pointer';
      hit.addEventListener('click', () => onLineClick(ln));
      svg.appendChild(hit);
      core.style.cursor = 'pointer';
      core.addEventListener('click', () => onLineClick(ln));
    }
    svg.appendChild(core);
  });

  // Central plot pin (drawn last so it sits above everything)
  const pinR = size >= 400 ? 12 : 5;
  const pinHalo = document.createElementNS(NS, 'circle');
  pinHalo.setAttribute('cx', CX); pinHalo.setAttribute('cy', CY);
  pinHalo.setAttribute('r', pinR + 3);
  pinHalo.setAttribute('fill', '#065f46');
  pinHalo.setAttribute('opacity', '0.18');
  svg.appendChild(pinHalo);
  const pin = document.createElementNS(NS, 'circle');
  pin.setAttribute('cx', CX); pin.setAttribute('cy', CY);
  pin.setAttribute('r', pinR);
  pin.setAttribute('fill', '#065f46');
  pin.setAttribute('stroke', '#ffffff');
  pin.setAttribute('stroke-width', size >= 400 ? '3' : '1.5');
  svg.appendChild(pin);
}

/**
 * Compact mini-map on the property card — tap anywhere to open the full-screen
 * zoom modal with clickable line details.
 */
const HTMiniMap = ({ centerLat, centerLng, lines, substations = [], towers = [], waterbodies = [], radiusM = 2000, style }) => {
  const svgRef = React.useRef(null);
  const [zoomOpen, setZoomOpen] = React.useState(false);
  const SIZE = 260;

  React.useEffect(() => {
    drawHTMap(svgRef.current, { centerLat, centerLng, lines, radiusM, size: SIZE });
  }, [centerLat, centerLng, lines, radiusM]);

  const closest = (lines || []).reduce(
    (best, l) => (best == null || l.distance_m < best.distance_m ? l : best),
    null,
  );

  return (
    <>
      <div
        className="mt-3 rounded-xl border border-slate-200 bg-white p-3 cursor-zoom-in hover:border-slate-300 hover:shadow-md transition-all"
        data-testid="ht-wire-minimap"
        onClick={() => setZoomOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setZoomOpen(true); } }}
        aria-label="Open full-screen HT corridor map"
      >
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <MapPin size={12} className="text-slate-500" />
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">
              HT corridor map (2 km radius)
            </span>
          </div>
          <span
            className="text-[9px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1"
            data-testid="ht-wire-minimap-zoom-hint"
          >
            <Maximize2 size={10} /> Tap to zoom
          </span>
        </div>

        <div className="flex items-center gap-3 text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-1">
          <span className="inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-600" /> ≤100m
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-600" /> ≤500m
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-600" /> &gt;500m
          </span>
        </div>

        <div className="relative flex justify-center">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            width="100%"
            style={{ maxWidth: SIZE, aspectRatio: '1 / 1' }}
            role="img"
            aria-label="Mini map showing high-tension lines around the plot"
          />
        </div>

        <div className={`mt-2 text-[10px] leading-relaxed ${style.text}`}>
          <span className="font-bold">Your plot</span> sits at the centre.
          {closest && (
            <> Closest HT line — <span className="font-bold">{closest.voltage_label}</span>{' '}
              at <span className="font-bold">
                {closest.distance_m < 1000
                  ? `${Math.round(closest.distance_m)} m`
                  : `${(closest.distance_m / 1000).toFixed(2)} km`}
              </span>.
            </>
          )}
          {' '}Tap the map to see full-size + clickable line details.
        </div>
      </div>

      <HTZoomModal
        open={zoomOpen}
        onClose={() => setZoomOpen(false)}
        centerLat={centerLat}
        centerLng={centerLng}
        lines={lines}
        substations={substations}
        towers={towers}
        waterbodies={waterbodies}
        radiusM={radiusM}
      />
    </>
  );
};

/**
 * Full-screen HT corridor map. Renders as a react-dom portal on document.body
 * so it escapes the property-detail modal stacking context. Tapping any HT
 * polyline highlights it and shows its full details in the right-hand panel.
 */
const HTZoomModal = ({ open, onClose, centerLat, centerLng, lines, substations = [], towers = [], waterbodies = [], radiusM = 2000 }) => {
  const svgRef = React.useRef(null);
  const [selected, setSelected] = React.useState(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // Lock body scroll while the modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  React.useEffect(() => {
    if (!open) return;
    // Wait one paint so the SVG has actual client size before we draw
    const raf = requestAnimationFrame(() => {
      const svg = svgRef.current;
      if (!svg) return;
      const size = Math.min(
        svg.clientWidth || 560,
        svg.clientHeight || 560,
      ) || 560;
      // Force square viewBox
      svg.setAttribute('viewBox', `0 0 ${size} ${size}`);
      drawHTMap(svg, {
        centerLat, centerLng, lines, radiusM, size,
        onLineClick: (ln) => setSelected(ln),
        highlightedId: selected?.id,
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [open, centerLat, centerLng, lines, radiusM, selected]);

  if (!open) return null;
  const sortedLines = [...(lines || [])].sort((a, b) => a.distance_m - b.distance_m);

  const badge = (ln) => {
    if (ln.distance_m <= 100) return { color: '#e11d48', label: 'Critical' };
    if (ln.distance_m <= 500) return { color: '#d97706', label: 'Warning' };
    return { color: '#0284c7', label: 'Info' };
  };

  const overlay = (
    <div
      className="fixed inset-0 z-[1200] flex items-stretch justify-center bg-slate-900/80 backdrop-blur-sm"
      data-testid="ht-wire-zoom-modal"
      onClick={onClose}
    >
      <div
        className="relative m-3 sm:m-6 flex-1 max-w-[1200px] rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col lg:flex-row"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-slate-900/85 text-white hover:bg-slate-800 inline-flex items-center justify-center shadow-lg"
          aria-label="Close full-screen HT map"
          data-testid="ht-wire-zoom-close"
        >
          <X size={16} />
        </button>

        {/* Left: big satellite map with draggable pin */}
        <div className="flex-1 min-w-0 bg-slate-50 border-b lg:border-b-0 lg:border-r border-slate-200 p-4 sm:p-6 flex flex-col">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <MapPin size={14} className="text-slate-600" />
            <h3 className="text-sm font-black uppercase tracking-widest text-slate-800">
              HT corridor · Satellite view · 2 km radius
            </h3>
            <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest bg-amber-100 border border-amber-300 text-amber-800 rounded-full px-2 py-0.5">
              <MapPin size={10} /> Drag pin to your exact plot
            </span>
          </div>
          <div className="relative flex-1 flex items-stretch justify-center min-h-[420px]">
            <HTSatelliteMap
              centerLat={centerLat}
              centerLng={centerLng}
              lines={lines}
              substations={substations}
              towers={towers}
              waterbodies={waterbodies}
              radiusM={radiusM}
              selectedId={selected?.id}
              onLineClick={(ln) => setSelected(ln)}
              height={520}
            />
          </div>
          <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
            <strong className="text-slate-700">Drag the yellow-halo pin</strong> onto the boundary of your plot.
            Every HT line&rsquo;s clearance recomputes in real time so you can see the exact distance
            from the wire to <em>your</em> land — not just to the plot centre.
          </p>
        </div>

        {/* Right: details panel */}
        <div className="w-full lg:w-[340px] flex-shrink-0 p-4 sm:p-5 overflow-y-auto bg-white">
          <div className="flex items-center gap-2 mb-3">
            <Zap size={13} className="text-amber-600" />
            <h4 className="text-xs font-black uppercase tracking-widest text-slate-700">
              Lines detected · {sortedLines.length}
            </h4>
          </div>

          {selected ? (
            <div
              className="rounded-xl border-2 p-3 mb-3"
              style={{ borderColor: badge(selected).color, background: `${badge(selected).color}0d` }}
              data-testid="ht-wire-zoom-selected"
            >
              <div className="flex items-center justify-between mb-2">
                <span
                  className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full text-white"
                  style={{ background: badge(selected).color }}
                >
                  {badge(selected).label}
                </span>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="text-[10px] text-slate-500 hover:text-slate-800 font-bold underline"
                >
                  clear
                </button>
              </div>
              <div className="text-lg font-black text-slate-900 leading-tight">
                {selected.voltage_label}
              </div>
              <div className="text-[11px] font-bold text-slate-700 mt-0.5">
                {selected.distance_m < 1000
                  ? `${Math.round(selected.distance_m)} m from your plot`
                  : `${(selected.distance_m / 1000).toFixed(2)} km from your plot`}
              </div>
              {selected.name && (
                <div className="text-[11px] text-slate-600 mt-1.5">
                  <span className="font-bold text-slate-700">Line: </span>{selected.name}
                </div>
              )}
              {selected.operator && (
                <div className="text-[11px] text-slate-600 mt-0.5">
                  <span className="font-bold text-slate-700">Operator: </span>{selected.operator}
                </div>
              )}
              {selected.circuits != null && (
                <div className="text-[11px] text-slate-600 mt-0.5">
                  <span className="font-bold text-slate-700">Circuits: </span>{selected.circuits}
                </div>
              )}
              <div className="mt-2 text-[11px] leading-relaxed text-slate-700 bg-white/70 rounded-lg p-2 border border-slate-200">
                {selected.advice ||
                  (badge(selected).label === 'Critical'
                    ? 'Line passes very close to the plot — recheck WBSEDCL/state utility safety-corridor rules before building.'
                    : badge(selected).label === 'Warning'
                    ? 'Line runs within safety buffer distance. Confirm floor-cap + corridor easement with the local municipal office.'
                    : 'Line is far enough that no construction restriction applies, but pin the exact corridor before signing.')}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-300 p-3 text-[11px] text-slate-500 leading-relaxed mb-3 bg-slate-50">
              Tap a coloured line on the map (or a row below) to see its voltage,
              operator and safety verdict here.
            </div>
          )}

          <div className="space-y-1.5" data-testid="ht-wire-zoom-list">
            {sortedLines.map((ln, i) => {
              const b = badge(ln);
              const isActive = selected?.id === ln.id;
              return (
                <button
                  key={ln.id || i}
                  type="button"
                  onClick={() => setSelected(ln)}
                  className={`w-full flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-all ${
                    isActive
                      ? 'border-slate-900 bg-slate-900 text-white shadow-md'
                      : 'border-slate-200 bg-white hover:border-slate-400 hover:bg-slate-50 text-slate-800'
                  }`}
                  data-testid={`ht-wire-zoom-line-${i}`}
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: b.color }}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[11px] font-black uppercase tracking-wide truncate">
                      {ln.voltage_label}
                    </span>
                    <span className={`block text-[10px] font-semibold truncate ${
                      isActive ? 'text-slate-300' : 'text-slate-500'
                    }`}>
                      {ln.name || 'Un-named corridor'}
                    </span>
                  </span>
                  <span className={`text-[10px] font-bold flex-shrink-0 ${
                    isActive ? 'text-white' : 'text-slate-700'
                  }`}>
                    {ln.distance_m < 1000
                      ? `${Math.round(ln.distance_m)} m`
                      : `${(ln.distance_m / 1000).toFixed(2)} km`}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document === 'undefined' ? null : createPortal(overlay, document.body);
};

const HighTensionBlock = ({ state, onRetry, centerLat, centerLng, waterbodies = [] }) => {
  const outerBase = 'mt-4 rounded-xl border p-4';
  const [voltageFilter, setVoltageFilter] = useState('all');

  if (state.loading) {
    return (
      <div className={`${outerBase} border-slate-200 bg-white/70 flex items-center gap-3`} data-testid="ht-wire-loading">
        <Loader2 size={16} className="animate-spin text-amber-600" />
        <div className="text-xs text-slate-600">Scanning OpenStreetMap for high-tension lines within 2 km…</div>
      </div>
    );
  }

  if (state.error || !state.data || state.data.checked === false) {
    const msg = state.error || state.data?.error || 'High-tension scan unavailable.';
    return (
      <div className={`${outerBase} border-amber-200 bg-amber-50`} data-testid="ht-wire-error">
        <div className="flex items-center gap-2 mb-1">
          <Zap size={16} className="text-amber-700" />
          <h4 className="text-sm font-black text-amber-900">High-tension wire scan unavailable</h4>
        </div>
        <div className="text-xs text-amber-800 leading-relaxed">{msg}</div>
        <button
          onClick={() => onRetry({ refresh: true })}
          className="mt-2 text-xs font-bold text-emerald-700 hover:text-emerald-900 underline inline-flex items-center gap-1"
          data-testid="ht-wire-retry"
        >
          <RefreshCw size={11} /> Retry scan
        </button>
      </div>
    );
  }

  const d = state.data;
  const activeMinKv = (HT_VOLTAGE_FILTERS.find(f => f.key === voltageFilter) || HT_VOLTAGE_FILTERS[0]).minKv;
  const allLines = d.lines || [];
  const lines = allLines.filter(l => (l.voltage_v || 0) >= activeMinKv * 1000);

  // Re-derive verdict / closest whenever the filter changes.
  const closest = lines.length ? lines.reduce((a, b) => (a.distance_m <= b.distance_m ? a : b)) : null;
  let effVerdict = d.verdict;
  let effLabel = d.verdict_label;
  let effNotes = d.verdict_notes;

  if (activeMinKv > 0) {
    if (!closest) {
      effVerdict = 'clear';
      effLabel = `No ${activeMinKv} kV+ line detected within 2 km`;
      effNotes = `OpenStreetMap shows no line at ${activeMinKv} kV or above in the 2 km search radius — smaller lines may still exist (toggle "All" to see them).`;
    } else {
      const dm = closest.distance_m;
      if (dm <= 100) {
        effVerdict = 'critical';
        effLabel = `⚠ ${activeMinKv} kV+ line passes over or beside this land`;
        effNotes = `A ${closest.voltage_label} line lies just ${dm.toFixed(0)} m from the plot pin.`;
      } else if (dm <= 500) {
        effVerdict = 'warning';
        effLabel = `${activeMinKv} kV+ line passes close to this land`;
        effNotes = `Nearest ${closest.voltage_label} line ~${dm.toFixed(0)} m away.`;
      } else {
        effVerdict = 'info';
        effLabel = `${activeMinKv} kV+ line in the neighbourhood`;
        effNotes = `Nearest ${closest.voltage_label} line ~${dm.toFixed(0)} m away — outside the ~50 m easement corridor.`;
      }
    }
  }

  const style = HT_VERDICT_STYLE[effVerdict] || HT_VERDICT_STYLE.info;

  return (
    <div className={`${outerBase} ${style.br} ${style.bg}`} data-testid="ht-wire-block">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Zap size={16} className={style.text} />
        <h4 className="text-sm font-black text-slate-900">High-tension wire scan</h4>
        <span className={`text-[9px] uppercase tracking-widest ${style.tag} text-white px-1.5 py-0.5 rounded font-bold`}>
          {effVerdict === 'clear' ? 'No HT wire' :
           effVerdict === 'critical' ? 'HT over land' :
           effVerdict === 'warning' ? 'HT nearby' :
           'HT in area'}
        </span>
        <button
          onClick={() => onRetry({ refresh: true })}
          className="ml-auto text-[10px] font-bold text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 border border-slate-300 bg-white/80 hover:bg-white rounded-full px-2 py-1 transition-colors"
          data-testid="ht-wire-rescan"
          title="Force a fresh scan against OpenStreetMap (may take up to 25 seconds)"
        >
          <RefreshCw size={10} /> Rescan
        </button>
      </div>

      {/* Voltage filter chips */}
      <div
        className="flex items-center gap-1 flex-wrap mb-3 text-[10px] font-bold"
        data-testid="ht-voltage-filter"
        role="tablist"
        aria-label="High-tension voltage filter"
      >
        <span className="text-slate-500 uppercase tracking-widest mr-1">Filter:</span>
        {HT_VOLTAGE_FILTERS.map(f => {
          const active = f.key === voltageFilter;
          const count = f.minKv === 0
            ? allLines.length
            : allLines.filter(l => (l.voltage_v || 0) >= f.minKv * 1000).length;
          return (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setVoltageFilter(f.key)}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full transition-colors ${
                active
                  ? `${style.tag} text-white shadow-sm`
                  : 'bg-white/70 text-slate-700 border border-slate-200 hover:bg-white'
              }`}
              data-testid={`ht-voltage-filter-${f.key}`}
            >
              {f.label}
              <span className={`text-[9px] tabular-nums ${active ? 'text-white/90' : 'text-slate-500'}`}>
                ({count})
              </span>
            </button>
          );
        })}
      </div>

      <div className={`text-sm font-bold ${style.text}`} data-testid="ht-wire-verdict-label">
        {effLabel}
      </div>
      <div className="text-[11px] text-slate-700 leading-relaxed mt-1">
        {effNotes}
      </div>

      {/* Mini map — visualises the plot pin + HT-line corridors within 2 km.
          Rendered even when 0 lines were detected so the user can still open
          the satellite view and drop a pin on their exact plot. */}
      {Number.isFinite(centerLat) && Number.isFinite(centerLng) && (
        <HTMiniMap
          centerLat={centerLat}
          centerLng={centerLng}
          lines={lines}
          substations={d.substations || []}
          towers={d.towers || []}
          waterbodies={waterbodies}
          radiusM={d.search_radius_m || 2000}
          style={style}
        />
      )}

      {lines.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="ht-wire-lines">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Detected HT lines in 2 km
            {activeMinKv > 0
              ? ` at ${activeMinKv} kV+ (${lines.length} of ${allLines.length})`
              : ` (${lines.length})`}
          </div>
          {lines.slice(0, 5).map((ln, i) => (
            <div
              key={ln.id || i}
              className="flex items-center gap-2 bg-white/80 border border-slate-200 rounded-lg px-2.5 py-1.5"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${style.dot} shrink-0`} />
              <span className="text-xs font-bold text-slate-800">{ln.voltage_label}</span>
              <span className="text-xs text-slate-600 truncate">
                {ln.name || 'transmission line'}
              </span>
              <span className="ml-auto text-xs font-semibold text-slate-700 shrink-0">
                {ln.distance_m < 1000 ? `${Math.round(ln.distance_m)} m` : `${(ln.distance_m/1000).toFixed(2)} km`}
              </span>
              {ln.over_land && (
                <span className="text-[9px] uppercase tracking-widest bg-rose-600 text-white px-1 py-0.5 rounded font-bold">
                  Over/near plot
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 text-[10px] text-slate-500 leading-relaxed border-t border-slate-200 pt-2">
        <span className="font-bold">Data source:</span> {d.source}. {d.advisory ||
          'OSM data can lag reality — always visit the site to physically confirm any HT-wire corridor before purchase.'}
      </div>
    </div>
  );
};

// ---- Similar plots ₹/sqft comparison bar chart ----
const SIMILAR_RADII = [50, 120, 250];

const SimilarRadiusToggle = ({ radius, onChangeRadius }) => (
  <div
    className="inline-flex rounded-full bg-slate-100 border border-slate-200 p-0.5 text-[10px] font-bold shrink-0"
    data-testid="similar-plots-radius-toggle"
    role="tablist"
  >
    {SIMILAR_RADII.map((r) => {
      const active = r === radius;
      return (
        <button
          key={r}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChangeRadius && onChangeRadius(r)}
          className={`px-2 py-1 rounded-full transition-colors ${
            active
              ? 'bg-emerald-600 text-white shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
          data-testid={`similar-plots-radius-${r}`}
        >
          {r} km
        </button>
      );
    })}
  </div>
);

const SimilarPlotsBlock = ({ state, onRetry, radius = 120, onChangeRadius }) => {
  const outerBase = 'mt-4 rounded-xl border p-4';

  if (state.loading) {
    return (
      <div className={`${outerBase} border-slate-200 bg-white/70`} data-testid="similar-plots-loading">
        <div className="flex items-center gap-3 flex-wrap">
          <Loader2 size={16} className="animate-spin text-emerald-600" />
          <div className="text-xs text-slate-600">Comparing this plot against nearby listings…</div>
          <div className="ml-auto"><SimilarRadiusToggle radius={radius} onChangeRadius={onChangeRadius} /></div>
        </div>
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className={`${outerBase} border-slate-200 bg-white/70`} data-testid="similar-plots-error">
        <div className="text-xs text-slate-600">
          {state.error || 'Comparison unavailable.'}
        </div>
        <button
          onClick={onRetry}
          className="mt-1 text-xs font-bold text-emerald-700 hover:text-emerald-900 underline inline-flex items-center gap-1"
        >
          <RefreshCw size={11} /> Retry
        </button>
      </div>
    );
  }

  const d = state.data;
  const rows = [];
  if (d.this_plot && d.this_plot.per_sqft) {
    rows.push({ ...d.this_plot, isThis: true });
  }
  (d.peers || []).forEach(p => rows.push({ ...p, isThis: false }));

  if (rows.length <= 1) {
    return (
      <div className={`${outerBase} border-slate-200 bg-white/70`} data-testid="similar-plots-empty">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <BarChart3 size={16} className="text-emerald-700" />
          <h4 className="text-sm font-black text-slate-900">Similar plots · ₹/sqft comparison</h4>
          <div className="ml-auto"><SimilarRadiusToggle radius={radius} onChangeRadius={onChangeRadius} /></div>
        </div>
        <div className="text-xs text-slate-600">
          Not enough comparable listings within <strong>{radius} km</strong> to plot yet.
          Try a wider radius or check back as we add more plots in this area.
        </div>
      </div>
    );
  }

  const maxPsf = Math.max(...rows.map(r => r.per_sqft || 0), 1);
  const verdictTone = d.verdict?.tone;
  const verdictClass =
    verdictTone === 'value'   ? 'bg-emerald-100 text-emerald-800 border-emerald-300' :
    verdictTone === 'premium' ? 'bg-amber-100 text-amber-800 border-amber-300' :
                                 'bg-sky-100 text-sky-800 border-sky-300';

  return (
    <div className={`${outerBase} border-emerald-200 bg-white/80`} data-testid="similar-plots-block">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <BarChart3 size={16} className="text-emerald-700" />
        <h4 className="text-sm font-black text-slate-900">Similar plots · ₹/sqft comparison</h4>
        {d.average_per_sqft && (
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 hidden sm:inline">
            Avg ₹{d.average_per_sqft.toLocaleString('en-IN')}/sqft
          </span>
        )}
        <div className="ml-auto"><SimilarRadiusToggle radius={radius} onChangeRadius={onChangeRadius} /></div>
      </div>

      {d.verdict && (
        <div className={`text-[11px] font-bold rounded-lg border px-2 py-1 mb-3 inline-block ${verdictClass}`} data-testid="similar-plots-verdict">
          {d.verdict.label}
        </div>
      )}

      <div className="space-y-2">
        {rows.map((r, i) => {
          const w = Math.max(6, Math.round((r.per_sqft / maxPsf) * 100));
          const bar = r.isThis
            ? 'bg-gradient-to-r from-emerald-600 to-emerald-500'
            : 'bg-gradient-to-r from-slate-400 to-slate-300';
          const psfTxt = r.per_sqft ? `₹${r.per_sqft.toLocaleString('en-IN')}` : '—';
          return (
            <div key={`sp-${r.id || i}`} className="text-xs">
              <div className="flex items-center gap-2 mb-0.5">
                <span className={`font-bold truncate ${r.isThis ? 'text-emerald-800' : 'text-slate-800'}`}>
                  {r.isThis ? 'This plot' : (r.property_code || r.title || 'Nearby plot')}
                </span>
                {!r.isThis && r.distance_km != null && (
                  <span className="text-[10px] text-slate-500 flex items-center gap-0.5">
                    <MapPin size={9} /> {r.distance_km} km
                  </span>
                )}
                <span className={`ml-auto font-mono font-bold ${r.isThis ? 'text-emerald-800' : 'text-slate-700'}`}>
                  {psfTxt}/sqft
                </span>
              </div>
              <div className="h-3 bg-slate-100 rounded overflow-hidden">
                <div
                  className={`h-full ${bar} rounded transition-all duration-500`}
                  style={{ width: `${w}%` }}
                />
              </div>
              {!r.isThis && (r.title || r.location) && (
                <div className="text-[10px] text-slate-500 truncate mt-0.5 pl-0.5">
                  {r.title}{r.location ? ` · ${r.location}` : ''}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-3 text-[10px] text-slate-500 leading-relaxed border-t border-slate-200 pt-2">
        {d.source}
      </div>
    </div>
  );
};



// ---- Why invest here — catalyst list ----
const WHY_ICON = {
  Plane, TrainFront, TrainTrack, Milestone, Construction, TrendingUp, Lightbulb,
};
// eslint-disable-next-line no-unused-vars
const _unused_Sparkles = Sparkles;
const WhyInvestBlock = ({ reasons }) => (
  <div className="mt-5" data-testid="locality-report-why-invest">
    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
      <Lightbulb size={11} className="text-amber-500" /> Why invest here
    </div>
    <ul className="space-y-2">
      {reasons.map((r, i) => {
        const Icon = WHY_ICON[r.icon] || CheckCircle2;
        return (
          <li key={`why-${i}`} className="flex gap-3 bg-white/70 border border-slate-200 rounded-xl px-3 py-2.5">
            <span className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100 flex items-center justify-center shrink-0">
              <Icon size={15} />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">{r.title}</div>
              <div className="text-xs text-slate-600 leading-relaxed">{r.detail}</div>
            </div>
          </li>
        );
      })}
    </ul>
  </div>
);

// ---- Future infrastructure block ----
const FutureInfraBlock = ({ items }) => (
  <div className="mt-5" data-testid="locality-report-future-infra">
    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
      <Construction size={11} className="text-amber-600" /> Upcoming growth catalysts (Gov. announced)
    </div>
    <ul className="grid sm:grid-cols-2 gap-2">
      {items.map((p, i) => (
        <li key={`fut-${i}`} className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-3 min-w-0">
          <span className="w-8 h-8 rounded-lg bg-amber-100 text-amber-800 border border-amber-300 flex items-center justify-center shrink-0">
            <Construction size={14} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-amber-900 truncate">
              {(p.name || '').replace('🚧 Upcoming · ', '')}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">
              Gov. announced
            </div>
          </div>
          <span className="text-xs font-bold text-amber-800 whitespace-nowrap shrink-0">
            {typeof p.distance_km === 'number' ? `${p.distance_km.toFixed(1)} km` : '—'}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

// ---- Road development block ----
const RoadDevelopmentBlock = ({ road, propertyLabel }) => {
  const score = Math.max(0, Math.min(10, Number(road.score) || 0));
  const tone = ROAD_TONE[road.label] || 'text-slate-700 bg-slate-50 border-slate-200';
  const highways = Array.isArray(road.nearby_highways) ? road.nearby_highways : [];
  const hasBearings = highways.some((h) => typeof h.bearing_deg === 'number');
  return (
    <div className="mt-4" data-testid="locality-report-road">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
        Road development
      </div>
      <div className={`rounded-xl border ${tone} px-4 py-3`}>
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold text-sm">{road.label}</div>
          <div className="text-xs font-bold">{score}/10</div>
        </div>
        <div className="w-full h-2 bg-white/60 rounded-full overflow-hidden">
          <div
            className="h-full bg-current opacity-70 rounded-full transition-all"
            style={{ width: `${score * 10}%` }}
            data-testid="locality-report-road-bar"
          />
        </div>
        {road.notes && (
          <div className="text-xs mt-2 opacity-90">{road.notes}</div>
        )}

        {/* Nearby highways with distances — 5 closest arterials */}
        {highways.length > 0 && (
          <div className="mt-3" data-testid="locality-report-nearby-highways">
            <div className="text-[10px] font-bold uppercase tracking-widest opacity-70 mb-1.5">
              Nearby highways
            </div>
            <ul className="grid sm:grid-cols-2 gap-1.5">
              {highways.map((h, i) => (
                <li
                  key={`hw-${i}`}
                  className="flex items-center gap-2 bg-white/70 rounded-lg px-2 py-1.5 border border-current/20"
                >
                  <Milestone size={13} className="opacity-70 shrink-0" />
                  <span className="text-xs font-medium truncate">{h.name}</span>
                  <span className="text-[11px] font-bold whitespace-nowrap ml-auto">
                    {typeof h.distance_km === 'number' ? `${h.distance_km.toFixed(1)} km` : '—'}
                  </span>
                </li>
              ))}
            </ul>

            {hasBearings && (
              <HighwayRadialMap highways={highways} propertyLabel={propertyLabel} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Commercial project ideas by land size ----
const CommercialIdeasBlock = ({ ideas, benchmark }) => {
  if (!ideas || !Array.isArray(ideas.ideas) || ideas.ideas.length === 0) return null;
  const bhk = ideas.bhk_pricing;
  const shops = ideas.shop_rates;
  return (
    <div className="mt-5" data-testid="locality-report-commercial-ideas">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
        <Building2 size={11} className="text-emerald-700" /> Commercial project ideas for this size
      </div>
      <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-3">
        <div className="text-xs font-semibold text-emerald-900 mb-1">
          Size band: <span className="text-emerald-700">{ideas.size_band}</span>
        </div>
        {ideas.headline && (
          <div className="text-sm font-bold text-slate-900 mb-2.5 leading-snug">
            {ideas.headline}
          </div>
        )}
        <ul className="space-y-2">
          {ideas.ideas.map((it, i) => (
            <li key={`ci-${i}`} className="flex gap-2.5 bg-white rounded-lg px-3 py-2 border border-emerald-100">
              <span className="w-10 h-10 rounded-md bg-emerald-700 text-white flex items-center justify-center shrink-0 text-[10px] font-black">
                {it.floors_or_scale}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-slate-900">{it.name}</div>
                <div className="text-[11px] text-slate-600 leading-relaxed">{it.notes}</div>
              </div>
            </li>
          ))}
        </ul>

        {bhk && Array.isArray(bhk.bhk) && bhk.bhk.length > 0 && (
          <div className="mt-3 pt-3 border-t border-emerald-100" data-testid="locality-report-bhk-pricing">
            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1.5">
              Achievable BHK sell price (state benchmark)
            </div>
            <ul className="space-y-1.5">
              {bhk.bhk.map((b, i) => (
                <li key={`bhk-${i}`} className="flex items-center gap-2 text-xs bg-white rounded-lg px-2 py-1.5 border border-emerald-100">
                  <span className="font-semibold text-slate-800 min-w-0 flex-1 truncate">{b.label}</span>
                  <span className="text-emerald-900 font-black whitespace-nowrap">
                    {formatINR(b.low)} – {formatINR(b.high)}
                  </span>
                </li>
              ))}
            </ul>
            <div className="text-[10px] text-slate-500 mt-1">
              Per sqft: ₹{bhk.per_sqft_low.toLocaleString()}–₹{bhk.per_sqft_high.toLocaleString()}
            </div>
          </div>
        )}

        {shops && (
          <div className="mt-3 pt-3 border-t border-emerald-100" data-testid="locality-report-shop-rates">
            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-1.5">
              Commercial shops / storage room rates
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-white border border-amber-100 p-2">
                <div className="text-[10px] uppercase text-slate-500 font-bold">Sell price</div>
                <div className="text-sm font-black text-amber-900">
                  ₹{shops.sell_per_sqft_low.toLocaleString()}–₹{shops.sell_per_sqft_high.toLocaleString()}
                </div>
                <div className="text-[10px] text-slate-500">per sqft (one-time)</div>
              </div>
              <div className="rounded-lg bg-white border border-amber-100 p-2">
                <div className="text-[10px] uppercase text-slate-500 font-bold">Monthly rent</div>
                <div className="text-sm font-black text-amber-900">
                  ₹{shops.rent_per_sqft_low}–₹{shops.rent_per_sqft_high}
                </div>
                <div className="text-[10px] text-slate-500">per sqft / month</div>
              </div>
            </div>
          </div>
        )}

        {benchmark && <StateBenchmarkStrip benchmark={benchmark} />}
      </div>
    </div>
  );
};

// ---- Flat / house — resale + rent focus ----
const FlatHouseInvestmentBlock = ({ investment }) => {
  const bestBadge = {
    resell: { label: 'Best for resale', color: 'bg-emerald-700 text-white' },
    rent:   { label: 'Best for rental', color: 'bg-amber-500 text-emerald-900' },
    either: { label: 'Both work',       color: 'bg-slate-700 text-white' },
  }[investment.best_bet] || { label: '', color: 'bg-slate-700 text-white' };
  const capX10 = investment.resale_10y / investment.current_price;
  const capX20 = investment.resale_20y / investment.current_price;
  return (
    <div className="mt-5" data-testid="locality-report-flat-house">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
        Best fit for this property
      </div>
      <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-amber-50/40 p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className={`text-[10px] uppercase tracking-widest ${bestBadge.color} px-2 py-0.5 rounded-full font-black`}>
            {bestBadge.label}
          </span>
          <span className="text-[11px] text-slate-600 leading-snug">{investment.bet_note}</span>
        </div>

        {/* Resale block */}
        <div className="rounded-lg bg-white border border-emerald-100 p-3 mb-3" data-testid="fh-resale">
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-2">
            Projected resale value
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <div className="text-[10px] uppercase text-slate-500 font-bold">Today</div>
              <div className="text-lg font-black text-slate-900">{formatINR(investment.current_price)}</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase text-slate-500 font-bold">In 10 yrs</div>
              <div className="text-lg font-black text-emerald-800">{formatINR(investment.resale_10y)}</div>
              <div className="text-[10px] text-emerald-700">{capX10.toFixed(2)}× money</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase text-slate-500 font-bold">In 20 yrs</div>
              <div className="text-lg font-black text-emerald-800">{formatINR(investment.resale_20y)}</div>
              <div className="text-[10px] text-emerald-700">{capX20.toFixed(2)}× money</div>
            </div>
          </div>
        </div>

        {/* Rental block */}
        <div className="rounded-lg bg-white border border-amber-100 p-3" data-testid="fh-rent">
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-2">
            Monthly rent if leased
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="rounded bg-amber-50 border border-amber-100 p-2">
              <div className="text-[10px] uppercase text-slate-500 font-bold">Conservative</div>
              <div className="text-lg font-black text-amber-900">
                {formatINR(investment.monthly_rent_low)}
                <span className="text-xs font-normal">/mo</span>
              </div>
              <div className="text-[10px] text-slate-500">₹{investment.rent_per_sqft_low}/sqft</div>
            </div>
            <div className="rounded bg-amber-50 border border-amber-100 p-2">
              <div className="text-[10px] uppercase text-slate-500 font-bold">Upside</div>
              <div className="text-lg font-black text-amber-900">
                {formatINR(investment.monthly_rent_high)}
                <span className="text-xs font-normal">/mo</span>
              </div>
              <div className="text-[10px] text-slate-500">₹{investment.rent_per_sqft_high}/sqft</div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-700">
            <span className="uppercase tracking-widest text-[10px] font-bold text-amber-800">
              Gross yield
            </span>
            <span className="font-black text-emerald-800">
              {investment.gross_yield_low_pct}% – {investment.gross_yield_high_pct}%
            </span>
            <span className="text-slate-500">
              (annual rent Rs {(investment.annual_rent_low / 100000).toFixed(1)}L – {(investment.annual_rent_high / 100000).toFixed(1)}L)
            </span>
          </div>
        </div>

        <div className="text-[10px] text-slate-500 mt-2 leading-relaxed">{investment.notes}</div>
      </div>
    </div>
  );
};

// ---- State-wise per-sqft benchmark ----
const StateBenchmarkStrip = ({ benchmark }) => (
  <div className="mt-3 pt-3 border-t border-emerald-100" data-testid="locality-report-state-benchmark">
    <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1.5">
      {benchmark.state} · commercial market benchmark
    </div>
    <div className="grid grid-cols-2 gap-2">
      <div className="rounded-lg bg-white border border-emerald-100 p-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Sell rate</div>
        <div className="text-sm font-black text-emerald-900">
          ₹{benchmark.sell_low.toLocaleString()}
          <span className="text-slate-500 font-normal"> – </span>
          ₹{benchmark.sell_high.toLocaleString()}
        </div>
        <div className="text-[10px] text-slate-500">per sqft (one-time)</div>
      </div>
      <div className="rounded-lg bg-white border border-emerald-100 p-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Rent rate</div>
        <div className="text-sm font-black text-emerald-900">
          ₹{benchmark.rent_low}
          <span className="text-slate-500 font-normal"> – </span>
          ₹{benchmark.rent_high}
        </div>
        <div className="text-[10px] text-slate-500">per sqft / month</div>
      </div>
    </div>
    <div className="text-[10px] text-slate-600 mt-1.5 leading-relaxed">{benchmark.note}</div>
  </div>
);

export default LocalityReportCard;
